import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Share2, Plus, Mail, CheckCircle, Clock, RefreshCw, Copy, Link2, Hash } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import api from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const REFERRAL_MEMBER_STEPS = {
  Referred: 'Register Interest',
  'Registered Interest': 'Company Registration',
  'Docs Submitted': 'Docs Review by Admin',
  'Verification Complete': 'Upgrade Member Tier',
  Successful: ''
};

const getReferralLifecycleStatus = (referral) => (
  referral.member_lifecycle_status
  || referral.affiliate_stage
  || 'Referred'
);

const getReferralPendingStep = (referral) => {
  const status = getReferralLifecycleStatus(referral);
  if (status === 'Successful') {
    return '-';
  }

  if (Object.prototype.hasOwnProperty.call(referral, 'pending_next_step')) {
    const pendingStep = referral.pending_next_step;
    if (!pendingStep || pendingStep === '-') return '-';
    if (pendingStep === 'Docs Review') return 'Docs Review by Admin';
    if (pendingStep === 'Upgrade Tier') return 'Upgrade Member Tier';
    return pendingStep;
  }

  if (Object.prototype.hasOwnProperty.call(REFERRAL_MEMBER_STEPS, status)) {
    return REFERRAL_MEMBER_STEPS[status] || '-';
  }

  return 'Register Interest';
};

const Referrals = () => {
  const { user } = useAuth();
  const memberId = user?.member_id;
  const { pointsSystemEnabled } = useTier();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showReferDialog, setShowReferDialog] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    successful: 0,
    pending: 0,
    total_points_earned: 0
  });
  
  const [referralForm, setReferralForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    notes: ''
  });

  const referralRows = useMemo(() => referrals.map((referral, index) => ({
    ...referral,
    srNo: index + 1,
    lifecycleStatus: getReferralLifecycleStatus(referral),
    pendingStep: getReferralPendingStep(referral)
  })), [referrals]);

  const fetchReferrals = useCallback(async () => {
    if (!memberId) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/member-referrals/${memberId}`);
      setReferrals(response.data.referrals || []);
      setReferralCode(response.data.referral_code || '');
      setReferralLink(response.data.referral_link || '');
      setStats(response.data.stats || {
        total: 0,
        successful: 0,
        pending: 0,
        total_points_earned: 0
      });
    } catch (error) {
      console.error('Failed to load referrals:', error);
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    if (memberId) {
      fetchReferrals();
    }
  }, [memberId, fetchReferrals]);

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const handleRefer = async (e) => {
    e.preventDefault();
    if (!memberId) {
      toast.error('Member profile is still loading');
      return;
    }
    if (!referralForm.company_name || !referralForm.contact_name || !referralForm.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      await api.post(`/api/member-referrals/${memberId}`, referralForm);
      toast.success(`Referral sent for ${referralForm.company_name}`);
      setShowReferDialog(false);
      setReferralForm({ company_name: '', contact_name: '', email: '', phone: '', notes: '' });
      fetchReferrals();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to submit referral'));
    } finally {
      setSubmitting(false);
    }
  };

  const getLifecycleBadge = (stage) => {
    const normalizedStage = stage || 'Referred';
    switch (normalizedStage) {
      case 'Successful':
        return (
          <Badge className="bg-green-100 text-green-700">
            <CheckCircle size={12} className="mr-1" />
            Active Member
          </Badge>
        );
      case 'Verification Complete':
        return (
          <Badge className="bg-indigo-100 text-indigo-700">
            <Clock size={12} className="mr-1" />
            Verification Complete
          </Badge>
        );
      case 'Docs Submitted':
        return (
          <Badge className="bg-amber-100 text-amber-700">
            <Clock size={12} className="mr-1" />
            Docs Submitted
          </Badge>
        );
      case 'Registered Interest':
        return (
          <Badge className="bg-blue-100 text-blue-700">
            <Clock size={12} className="mr-1" />
            Registered Interest
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-700">
            <Clock size={12} className="mr-1" />
            Referred
          </Badge>
        );
    }
  };

  const formatDate = (value) => {
    if (!value) return '';
    try {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
    } catch (e) {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Refer a Partner</h1>
          <p className="text-gray-600 mt-1">Loading your referrals...</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-20 bg-gray-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Refer a Partner</h1>
          <p className="text-gray-600 mt-1">
            {pointsSystemEnabled ? 'Grow the network and earn rewards' : 'Grow the network by inviting trusted partners'}
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={fetchReferrals}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Dialog open={showReferDialog} onOpenChange={setShowReferDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-referral-btn">
                <Plus size={18} className="mr-2" />
                New Referral
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] max-w-2xl flex flex-col w-[95vw] sm:w-full">
              <DialogHeader>
                <DialogTitle>Refer a Logistics Partner</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto flex-1 min-h-0 px-2">
              <form onSubmit={handleRefer} className="space-y-4">
                {referralCode && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <Label className="text-gray-600">Your referral code</Label>
                    <p className="mt-1 font-mono font-semibold text-gray-900">{referralCode}</p>
                  </div>
                )}
                <div>
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    placeholder="Enter company name"
                    value={referralForm.company_name}
                    onChange={(e) => setReferralForm({ ...referralForm, company_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="contact_name">Contact Person *</Label>
                  <Input
                    id="contact_name"
                    placeholder="Full name"
                    value={referralForm.contact_name}
                    onChange={(e) => setReferralForm({ ...referralForm, contact_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="contact@company.com"
                    value={referralForm.email}
                    onChange={(e) => setReferralForm({ ...referralForm, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone (Optional)</Label>
                  <Input
                    id="phone"
                    placeholder="+1 234 567 8900"
                    value={referralForm.phone}
                    onChange={(e) => setReferralForm({ ...referralForm, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Any additional information..."
                    value={referralForm.notes}
                    onChange={(e) => setReferralForm({ ...referralForm, notes: e.target.value })}
                    rows={2}
                  />
                </div>
                {pointsSystemEnabled ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900 font-medium">Referral Reward: 500 Points</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Earn reward points when your referral completes tier payment
                    </p>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900 font-medium">Referral Benefit</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Invite trusted partners to expand your network in the alliance
                    </p>
                  </div>
                )}
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowReferDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={submitting}
                  >
                    <Share2 size={16} className="mr-2" />
                    {submitting ? 'Submitting...' : 'Send Referral'}
                  </Button>
                </div>
              </form>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {(referralCode || referralLink) && (
        <Card className="overflow-hidden border-0 shadow-md bg-gradient-to-br from-indigo-50 via-white to-blue-50 ring-1 ring-indigo-100/80">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
                <Share2 size={20} />
              </div>
              <div>
                <CardTitle className="text-lg text-gray-900">Your referral code & link</CardTitle>
                <p className="text-sm text-gray-500 mt-0.5">Share these so others can register with your code</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {referralCode && (
              <div className="rounded-xl bg-white/80 p-4 ring-1 ring-gray-200/80 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Hash size={14} className="text-indigo-500" />
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Referral code</span>
                </div>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="rounded-lg bg-indigo-600 px-4 py-2.5">
                    <p className="text-lg font-mono font-bold tracking-wide text-white select-all">{referralCode}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-300"
                    onClick={() => copyToClipboard(referralCode, 'Referral code')}
                  >
                    <Copy size={14} className="mr-1.5" />
                    Copy code
                  </Button>
                </div>
              </div>
            )}
            {referralLink && (
              <div className="rounded-xl bg-white/80 p-4 ring-1 ring-gray-200/80 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Link2 size={14} className="text-indigo-500" />
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Referral link</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="flex-1 min-w-0 text-sm font-mono text-gray-700 bg-gray-100 rounded-lg px-3 py-2.5 truncate ring-1 ring-gray-200" title={referralLink}>
                    {referralLink}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-300 shrink-0"
                    onClick={() => copyToClipboard(referralLink, 'Referral link')}
                  >
                    <Copy size={14} className="mr-1.5" />
                    Copy link
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-900">Successful Referrals</p>
                <p className="text-3xl font-bold text-green-700 mt-1">{stats.successful}</p>
              </div>
              <CheckCircle size={40} className="text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-900">Pending Referrals</p>
                <p className="text-3xl font-bold text-amber-700 mt-1">{stats.pending}</p>
              </div>
              <Clock size={40} className="text-amber-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">
                  {pointsSystemEnabled ? 'Points Earned' : 'Network Growth'}
                </p>
                <p className="text-3xl font-bold text-blue-700 mt-1">
                  {pointsSystemEnabled ? stats.total_points_earned : stats.successful}
                </p>
              </div>
              <Share2 size={40} className="text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Referred Members ({stats.total})</CardTitle>
        </CardHeader>
        <CardContent>
          {referralRows.length === 0 ? (
            <div className="text-center py-8">
              <Share2 size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No referrals yet</p>
              <p className="text-sm text-gray-400 mt-1">Start referring partners to earn rewards!</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="hidden overflow-x-auto rounded-lg border border-gray-200 md:block">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-20 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Sr no.</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Referred member</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Member Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Pending Next Step</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Referred on</th>
                      {pointsSystemEnabled && (
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Reward</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {referralRows.map((referral) => (
                      <tr key={referral.referral_id || referral.email} className="hover:bg-gray-50">
                        <td className="px-4 py-4 align-top font-medium text-gray-700">{referral.srNo}</td>
                        <td className="px-4 py-4 align-top">
                          <div>
                            <p className="font-semibold text-gray-900">{referral.company_name}</p>
                            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                              <span>{referral.contact_name}</span>
                              <span className="inline-flex items-center">
                                <Mail size={13} className="mr-1" />
                                {referral.email}
                              </span>
                            </div>
                            {referral.lifecycleStatus === 'Successful' && referral.successful_at && (
                              <p className="mt-1 text-xs font-medium text-green-600">
                                Activated on {formatDate(referral.successful_at)}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top">
                          {getLifecycleBadge(referral.lifecycleStatus)}
                        </td>
                        <td className="px-4 py-4 align-top">
                          <span className="font-medium text-gray-900">{referral.pendingStep}</span>
                        </td>
                        <td className="px-4 py-4 align-top text-gray-600">
                          {formatDate(referral.referral_date || referral.created_at) || '-'}
                        </td>
                        {pointsSystemEnabled && (
                          <td className="px-4 py-4 text-right align-top">
                            <span className={referral.lifecycleStatus === 'Successful' && referral.reward_earned > 0 ? 'font-bold text-green-600' : 'text-gray-500'}>
                              {referral.lifecycleStatus === 'Successful' && referral.reward_earned > 0 ? `+${referral.reward_earned}` : '-'}
                            </span>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-3 md:hidden">
                {referralRows.map((referral) => (
                  <div
                    key={referral.referral_id || referral.email}
                    className="flex flex-col gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors lg:flex-row lg:items-start lg:justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-start gap-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
                          {referral.srNo}
                        </span>
                        <div>
                          <h3 className="font-semibold text-gray-900">{referral.company_name}</h3>
                          <p className="text-xs text-gray-500 mt-1">
                            Referred on {formatDate(referral.referral_date || referral.created_at) || '-'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                        <span>{referral.contact_name}</span>
                        <span className="flex items-center">
                          <Mail size={14} className="mr-1" />
                          {referral.email}
                        </span>
                      </div>
                      {referral.lifecycleStatus === 'Successful' && referral.successful_at && (
                        <p className="text-xs text-green-600 font-medium mt-1">
                          Activated on {formatDate(referral.successful_at)}
                        </p>
                      )}
                    </div>

                    <div className="flex min-w-[15rem] flex-col gap-2 lg:items-end">
                      {getLifecycleBadge(referral.lifecycleStatus)}
                      <p className="text-xs text-gray-600 lg:text-right">
                        <span className="font-medium text-gray-700">Next step pending:</span>{' '}
                        {referral.pendingStep}
                      </p>
                      {pointsSystemEnabled && referral.lifecycleStatus === 'Successful' && referral.reward_earned > 0 && (
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">+{referral.reward_earned}</p>
                          <p className="text-xs text-gray-500">points</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200">
        <CardContent className="p-8">
          <div className="text-center max-w-2xl mx-auto">
            <Share2 size={48} className="mx-auto mb-4 text-purple-600" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3">How Referrals Work</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold text-purple-600">1</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Refer a Partner</h4>
                <p className="text-sm text-gray-600">Submit details of a logistics company you'd like to invite</p>
              </div>
              <div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold text-blue-600">2</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">They Get Reviewed</h4>
                <p className="text-sm text-gray-600">We verify and review the company for alliance membership</p>
              </div>
              <div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold text-green-600">3</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  {pointsSystemEnabled ? 'Earn Rewards' : 'Grow Your Network'}
                </h4>
                <p className="text-sm text-gray-600">
                  {pointsSystemEnabled
                    ? 'Get 500 reward points when they complete tier payment'
                    : 'Strengthen your network when trusted partners activate membership'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Referrals;
